import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { contentStyles, paragraphRegular } from '@helpers/typography';
import svgChevron from '@img/icons/background/chevron-right.svg?url';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import DatePickerStyles from '@stories/Components/Global/DatePicker/DatePicker.styles';
import FormStyles from '@stories/Components/Global/Forms/Form.styles';
import TextboxStyles from '@stories/Components/Global/Inputs/Textbox/Textbox.styles';
import { ModalContentContainer, ModalContentInner } from '@stories/Components/Global/Modal/Modal.styles';
import QuantitySelectorStyles from '@stories/Components/Global/QuantitySelector/QuantitySelector.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';
const Wrapper = styled.div `
  --contentPadding: ${fluid(12, 16)};

  &[data-site='ess'] {
    --borderColor: ${brand.ess.secondary.lightGrey};
    --idColour: ${brand.ess.secondary.darkGrey};
    --overlayBgColour: ${rgba(brand.global.red, 0.8)};
    --overlayFgColour: ${brand.global.white};
    --priceColour: ${brand.global.red};
    --textColour: ${brand.ess.primary};
  }

  &[data-site='mep'] {
    --borderColor: ${brand.mep.secondary.lightGrey};
    --idColour: ${brand.mep.secondary.darkestGrey};
    --overlayBgColour: ${rgba(brand.global.red, 0.8)};
    --overlayFgColour: ${brand.global.white};
    --priceColour: ${brand.global.red};
    --textColour: ${brand.mep.primary};
  }

  ${FormStyles.ErrorList} {
    border: 2px solid var(--borderColor);
    border-bottom: none;
    padding: var(--contentPadding);
  }
`;
const Container = styled.div `
  position: relative;
  border: 2px solid var(--borderColor);
  display: flex;
  overflow: hidden;

  ${Wrapper}[data-site='mep'] & {
    border-radius: 8px;
  }
`;
const ImageWrapper = styled.div `
  position: relative;
  padding: ${fluid(6, 12)};
  width: 30%;
  min-width: 108px;
  aspect-ratio: 1;

  @media ${until(Device.Tablet)} {
    ${Container}[data-variant='full'] > & {
      display: none;
    }

    ${Container}[data-variant='full'] & {
      border-right: 2px solid var(--borderColor);
      border-bottom: 2px solid var(--borderColor);
      margin: calc(var(--contentPadding) * -1) 0 0 calc(var(--contentPadding) * -1);
    }
  }

  @media ${from(Device.DesktopSmall)} {
    max-height: 166px;
    max-width: 195px;

    ${Container}[data-site='ess'] & {
      margin: auto 0;
    }
  }
`;
const Image = styled.img `
  ${imageCoverContainer()}
  object-fit: contain;

  @media ${until(Device.Tablet)} {
    ${Container}[data-site='mep'][data-variant='overlay'] & {
      object-position: top center;
    }
  }
`;
const ContentWrapper = styled.div `
  flex: 1;
  padding: var(--contentPadding);
  border-left: 2px solid var(--borderColor);
  display: flex;
  flex-direction: column;

  ${Container}[data-site='mep'] & {
    ${QuantitySelectorStyles.Container} {
      --bgColour: ${brand.mep.secondary.lightestGrey};
    }
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-variant='full'] & {
      border: 0;
    }
  }
`;
const PriceWrapper = styled.div `
  display: flex;
  gap: 5px;
  align-items: flex-end;
  color: var(--priceColour);
  white-space: nowrap;
`;
const Price = styled.span `
  font-size: ${fluid(16, 18)};
  line-height: 24px;

  ${Container}[data-site='ess'] & {
    ${fonts.daxWide.bold};
  }

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.bold};
  }
`;
const Suffix = styled.span `
  ${fonts.daxWide.regular};

  font-size: ${fluid(10, 12)};
  line-height: ${fluid(18, 20)};
  text-transform: uppercase;
`;
const TopWrapper = styled.div `
  display: flex;
  gap: ${fluid(12, 16)} ${fluid(10, 24)};
  align-items: flex-start;

  ${Container}[data-variant='full'] & {
    margin-bottom: 14px;

    @media ${from(Device.Tablet)} {
      margin: 0;
      justify-content: space-between;

      ${ImageWrapper} {
        display: none;
      }
    }
  }
`;
const TitlePriceWrapper = styled.div `
  ${Container}[data-variant='overlay'] & {
    > ${PriceWrapper} {
      margin-bottom: 14px;

      @media ${from(Device.Tablet)} {
        display: none;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    display: contents;
  }
`;
const Title = styled.h3 `
  ${paragraphRegular};
  ${fonts.daxWide.bold};

  margin: 0 0 4px;
`;
const Id = styled.span `
  ${paragraphRegular};

  color: var(--idColour);
  margin: 0 0 10px;
`;
const AccordionWrapper = styled.div `
  > ${ButtonStyles.Container} {
    margin: 0 0 12px;

    ${ButtonStyles.IconWrapper} {
      transform: none !important;

      svg {
        ${transition('transform', 0.2)}
      }
    }
  }

  > ${ButtonStyles.Container}[data-active='false'] {
    ${ButtonStyles.IconWrapper} svg {
      transform: rotate(90deg);
    }
  }

  > ${ButtonStyles.Container}[data-active='true'] {
    ${ButtonStyles.IconWrapper} svg {
      transform: rotate(-90deg);
    }
  }
`;
const AccordionArea = styled(m.div) `
  overflow: hidden;
  position: relative;
  margin: 0 0 4px;
`;
const Content = styled.div `
  ${contentStyles};

  ${Container}[data-site='ess'] & {
    ul > li::before {
      color: transparent;
      background-image: url(${svgChevron});
      background-repeat: no-repeat;
      background-position: center left;
    }
  }
`;
const BottomWrapper = styled.div `
  display: flex;
  gap: ${fluid(12, 16)} 24px;
  flex-direction: column;

  ${FormStyles.Field} {
    position: relative;
  }

  ${TextboxStyles.Input} {
    border: 0;
    height: 46px;
    line-height: 46px;

    &:hover {
      cursor: pointer;
    }

    ${Container}[data-site= 'ess'] & {
      border-radius: 0;
      background-color: ${rgba(brand.ess.secondary.midGrey, 0.1)};
    }

    ${Container}[data-site='mep'] & {
      background-color: ${brand.mep.secondary.lightestGrey};
    }
  }

  @media ${until(Device.Tablet)} {
    ${FormStyles.Row} {
      --count: 2;

      gap: 12px;
    }

    ${Container}[data-variant='full'] & {
      border-top: 1px solid var(--borderColor);
      padding-top: 8px;
    }

    ${Container}[data-variant='overlay'] & {
      ${PriceWrapper} {
        display: none;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    align-items: flex-end;
    flex-direction: row;
    margin-top: auto;

    ${Container}[data-variant='full'] & {
      justify-content: space-between;
    }

    &[data-has-datepicker='true'] {
      flex-wrap: wrap;

      ${FormStyles.Row} {
        max-width: min(70%, 376px);
      }
    }
  }
`;
const TotalPrice = styled.div `
  ${paragraphRegular};
  ${fonts.daxWide.bold};

  text-transform: uppercase;
  display: block;
  margin: 0;
  color: var(--textColor);

  ${BottomWrapper}[data-has-datepicker='true'] & {
    width: 100%;
  }
`;
const CalendarIcon = styled.span `
  pointer-events: none;
  display: block;
  position: absolute;
  right: 12px;
  bottom: 14px;

  > svg {
    display: block;
    width: 18px;
  }

  ${Container}[data-site='ess'] & {
    color: ${brand.ess.primary};
  }

  ${Container}[data-site='mep'] & {
    color: ${brand.mep.primary};
  }
`;
const QuantityPriceWrapper = styled.div `
  display: flex;
  align-items: flex-end;

  @media ${from(Device.Tablet)} {
    display: contents;
  }
`;
const DeleteButton = styled.button `
  ${ButtonReset};

  margin-left: auto;
  display: grid;
  place-content: center;
  padding: ${fluid(12, 16)};
  background-color: var(--borderColor);
  color: var(--textColour);

  .focus-ring & {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }

  > svg {
    display: block;
    width: ${fluid(14, 18)};
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-variant='full'] > & {
      display: none;
    }
  }

  @media ${from(Device.Tablet)} {
    ${BottomWrapper} & {
      display: none;
    }
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    span& {
      button:active &,
      a:active & {
        transform: translateY(1.5px);
      }
    }

    &:not(span) {
      &:active {
        transform: translateY(1.5px);
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity', 0.15)};

    svg {
      ${transition('transform', 0.15)};
    }

    &.focus-ring,
    &:hover {
      opacity: 0.9;

      svg {
        transform: scale(1.1);
      }
    }
  }
`;
const RemovedOverlay = styled.div `
  position: relative;
  display: flex;
  background: var(--overlayBgColour);
  color: var(--overlayFgColour);
  flex-direction: column;
  height: 100%;
  place-content: center;
  place-items: center;
  text-align: center;
  width: 100%;
  z-index: 8;
  padding: ${fluid(24, 32)};
  position: absolute;
  top: 0;
  left: 0;
`;
const RemovedOverlayText = styled.span `
  ${paragraphRegular};

  display: block;
  margin: 0;
`;
const AddToBasketModal = styled(ModalContentContainer) ``;
const AddToBasketModalContent = styled(ModalContentInner) `
  ${DatePickerStyles.Wrapper} {
    margin: 0 0 ${fluid(20, 40)};
  }

  /* Adjust button width to compensate for active state */

  ${Container}[data-site='ess'] & {
    @media ${until(Device.TabletLarge)} {
      ${ButtonStyles.Container} {
        width: calc(100% - 30px);
      }

      ${ButtonStyles.Text} {
        transform: translateX(15px);
      }
    }
  }
`;
const ModalButtonWrapper = styled.div `
  text-align: center;
`;
const CartProductCardStyles = {
    AccordionArea,
    AccordionWrapper,
    AddToBasketModal,
    AddToBasketModalContent,
    BottomWrapper,
    CalendarIcon,
    Container,
    Content,
    ContentWrapper,
    DeleteButton,
    Id,
    Image,
    ImageWrapper,
    ModalButtonWrapper,
    Price,
    PriceWrapper,
    QuantityPriceWrapper,
    RemovedOverlay,
    RemovedOverlayText,
    Suffix,
    Title,
    TitlePriceWrapper,
    TopWrapper,
    TotalPrice,
    Wrapper,
};
export default CartProductCardStyles;
